import { useState, useEffect } from 'react';
import { Sparkles, X, Flame, CheckCircle2, Loader2 } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { API_URL } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface DailyPrompt {
    id: string;
    prompt: string;
    genre: string | null;
    difficulty: string;
    bonus_multiplier: number;
}

export function DailyPrompt() {
    const { themeProfile } = useAppStore();
    const [isVisible, setIsVisible] = useState(false);
    const [prompt, setPrompt] = useState<DailyPrompt | null>(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [streak, setStreak] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isCompleting, setIsCompleting] = useState(false);
    const [wordsWritten, setWordsWritten] = useState('');
    const [showCompleteForm, setShowCompleteForm] = useState(false);

    useEffect(() => {
        fetchTodayPrompt();
        fetchStreak();
    }, []);

    const fetchTodayPrompt = async () => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/prompts/today`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.prompt) {
                setPrompt(data.prompt);
                setIsCompleted(data.completed);
                // Only show if not completed and it's the start of a session
                if (!data.completed) {
                    const hasSeenToday = sessionStorage.getItem('prompt_seen_today');
                    if (!hasSeenToday) {
                        setIsVisible(true);
                        sessionStorage.setItem('prompt_seen_today', 'true');
                    }
                }
            }
        } catch (e) {
            console.error('Failed to fetch prompt:', e);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStreak = async () => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/prompts/streak`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setStreak(data.streak || 0);
        } catch (e) {
            console.error('Failed to fetch streak:', e);
        }
    };

    const handleComplete = async () => {
        if (!prompt) return;
        setIsCompleting(true);
        
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/prompts/${prompt.id}/complete`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ wordsWritten: parseInt(wordsWritten) || 0 })
            });
            
            if (res.ok) {
                setIsCompleted(true);
                setShowCompleteForm(false);
                fetchStreak();
                setTimeout(() => setIsVisible(false), 2000);
            }
        } catch (e) {
            console.error('Failed to complete prompt:', e);
        } finally {
            setIsCompleting(false);
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'easy': return 'text-green-600 bg-green-100';
            case 'hard': return 'text-red-600 bg-red-100';
            default: return 'text-amber-600 bg-amber-100';
        }
    };

    if (isLoading || !prompt || isCompleted) return null;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    className="fixed bottom-6 right-6 z-50 max-w-md"
                >
                    <div 
                        className="rounded-3xl border shadow-2xl overflow-hidden"
                        style={{ 
                            backgroundColor: themeProfile.paperSurface, 
                            borderColor: themeProfile.border,
                            background: `linear-gradient(135deg, ${themeProfile.paperSurface} 0%, ${themeProfile.background} 100%)`
                        }}
                    >
                        {/* Header */}
                        <div 
                            className="px-6 py-4 flex items-center justify-between"
                            style={{ backgroundColor: `${themeProfile.accent}15` }}
                        >
                            <div className="flex items-center gap-3">
                                <div 
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: themeProfile.accent }}
                                >
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm" style={{ color: themeProfile.text }}>
                                        Daily Writing Prompt
                                    </h3>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <Flame className="w-3.5 h-3.5 text-orange-500" />
                                        <span className="text-xs font-medium" style={{ color: themeProfile.text }}>
                                            {streak} day streak
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsVisible(false)}
                                className="p-1.5 rounded-lg hover:bg-black/10 transition-colors"
                            >
                                <X className="w-4 h-4" style={{ color: themeProfile.text }} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <span className={cn(
                                    "px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                                    getDifficultyColor(prompt.difficulty)
                                )}>
                                    {prompt.difficulty}
                                </span>
                                {prompt.genre && (
                                    <span 
                                        className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                        style={{ backgroundColor: `${themeProfile.accent}20`, color: themeProfile.accent }}
                                    >
                                        {prompt.genre}
                                    </span>
                                )}
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-600">
                                    +{prompt.bonus_multiplier}x bonus
                                </span>
                            </div>

                            <p className="text-base leading-relaxed mb-4" style={{ color: themeProfile.text }}>
                                "{prompt.prompt}"
                            </p>

                            {!showCompleteForm ? (
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowCompleteForm(true)}
                                        className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                                        style={{ backgroundColor: themeProfile.accent }}
                                    >
                                        I Wrote This!
                                    </button>
                                    <button
                                        onClick={() => setIsVisible(false)}
                                        className="px-4 py-3 rounded-xl font-medium text-sm hover:bg-black/5 transition-colors"
                                        style={{ color: themeProfile.text }}
                                    >
                                        Later
                                    </button>
                                </div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="space-y-3"
                                >
                                    <div>
                                        <label className="text-xs font-medium opacity-60 mb-1.5 block">
                                            How many words did you write?
                                        </label>
                                        <input
                                            type="number"
                                            value={wordsWritten}
                                            onChange={(e) => setWordsWritten(e.target.value)}
                                            placeholder="500"
                                            className="w-full px-4 py-2.5 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-amber-500/20 text-sm"
                                            style={{ borderColor: themeProfile.border }}
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleComplete}
                                            disabled={isCompleting}
                                            className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                                        >
                                            {isCompleting ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <CheckCircle2 className="w-4 h-4" />
                                            )}
                                            Complete
                                        </button>
                                        <button
                                            onClick={() => setShowCompleteForm(false)}
                                            className="px-4 py-3 rounded-xl font-medium text-sm hover:bg-black/5 transition-colors"
                                            style={{ color: themeProfile.text }}
                                        >
                                            Back
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* Progress indicator */}
                        <div 
                            className="h-1 w-full"
                            style={{ backgroundColor: `${themeProfile.accent}20` }}
                        >
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: isCompleted ? '100%' : '0%' }}
                                className="h-full transition-all duration-500"
                                style={{ backgroundColor: themeProfile.accent }}
                            />
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// Compact version for dashboard
export function DailyPromptCard() {
    const { themeProfile, setViewMode } = useAppStore();
    const [prompt, setPrompt] = useState<DailyPrompt | null>(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [streak, setStreak] = useState(0);

    useEffect(() => {
        fetchTodayPrompt();
        fetchStreak();
    }, []);

    const fetchTodayPrompt = async () => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/prompts/today`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.prompt) {
                setPrompt(data.prompt);
                setIsCompleted(data.completed);
            }
        } catch (e) {
            console.error('Failed to fetch prompt:', e);
        }
    };

    const fetchStreak = async () => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/prompts/streak`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setStreak(data.streak || 0);
        } catch (e) {
            console.error('Failed to fetch streak:', e);
        }
    };

    if (!prompt) return null;

    return (
        <div 
            className="rounded-2xl border p-5 cursor-pointer transition-all hover:scale-[1.02] group"
            style={{ 
                backgroundColor: themeProfile.paperSurface, 
                borderColor: themeProfile.border,
                background: `linear-gradient(135deg, ${themeProfile.paperSurface} 0%, ${themeProfile.background} 100%)`
            }}
            onClick={() => setViewMode('manuscript')}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: isCompleted ? '#22c55e' : themeProfile.accent }}
                    >
                        {isCompleted ? (
                            <CheckCircle2 className="w-4 h-4 text-white" />
                        ) : (
                            <Sparkles className="w-4 h-4 text-white" />
                        )}
                    </div>
                    <div>
                        <h3 className="font-bold text-sm" style={{ color: themeProfile.text }}>
                            {isCompleted ? 'Prompt Completed!' : 'Daily Prompt'}
                        </h3>
                        <div className="flex items-center gap-2">
                            <Flame className="w-3 h-3 text-orange-500" />
                            <span className="text-xs" style={{ color: themeProfile.text }}>
                                {streak} day streak
                            </span>
                            {!isCompleted && (
                                <span className="text-xs text-purple-600 font-medium">
                                    +{prompt.bonus_multiplier}x bonus
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {isCompleted && (
                    <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                        Done
                    </span>
                )}
            </div>

            {!isCompleted && (
                <>
                    <p className="text-sm line-clamp-2 mb-3 opacity-80" style={{ color: themeProfile.text }}>
                        "{prompt.prompt}"
                    </p>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium opacity-50 group-hover:opacity-100 transition-opacity">
                            Click to start writing →
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
